/**
 * Process Flow (obj_type "L") transport.
 *
 * The flow is an authoring object: saving it compiles each work edge into a
 * managed work rule on its source transaction, which is what the response's
 * `compiled.objects` reports back so the designer can say which objects were
 * written to.
 *
 * Two payloads ride along with every read, and both exist so the designer can
 * be specific rather than vague. The `palette` is everything that may be put on
 * a diagram — objects, tables, other flows, and the COLUMNS a record match can
 * be written from. The `review` is every problem, each one carrying the node or
 * arrow it is about, so a warning can be clicked rather than hunted for.
 */

import api from './client'

/** What a box IS, beside which object it points at.
 *
 *  `step`, `entity` and `store` are all ORDINARY TRANSACTION OBJECTS — what
 *  separates them is how the process treats them: somebody works through a
 *  step, reads an entity, and the process writes a store without anybody
 *  typing into it. That is a fact about the diagram, not about the object. */
export type NodeKind = 'step' | 'entity' | 'store' | 'process' | 'external'

export interface FlowNode {
  id: string
  obj_name: string
  /** Which kind of application object — T / V / G / R …, or the placeholders
   *  L (another process) and EXT (a party outside the app). A step, an entity
   *  and a data store are all ordinary objects and keep their own type. */
  obj_type: string
  kind?: NodeKind
  /** What the application calls this step — its navigation title, else its own
   *  form title. The object name is `obj_name`, and is not a name. */
  label: string
  /** Present on DERIVED nodes: the menu entry's description and icon. */
  description?: string
  icon?: string
  /** The designer's own note about this box. */
  note?: string
  /** process: the flow handed to, and the step it starts at. */
  flow?: string
  entry_obj?: string
  x: number
  y: number
}

export interface FlowRule {
  name?: string
  title?: string
  summary?: string
  condition?: string
  trigger?: { kind?: string; anchor?: string; offset_days?: number }
  assign?: { roles?: string[]; user_columns?: string[]; users?: string[] }
  due?: { column?: string; warn_days?: number }
  priority?: string
  target?: {
    open?: string
    obj_name?: string
    form_no?: number
    verb?: string
    seed?: Record<string, string>
  }
  suppress_when?: string[]
}

export interface FlowEdge {
  id: string
  /** Authored kinds only; derived edges arrive separately and are read-only.
   *  `work` is the next step, `link` a related record, `update` a write into a
   *  data store and `read` a look-up of one. */
  kind: 'work' | 'link' | 'update' | 'read' | string
  from: string
  to: string
  label?: string
  /** The columns two RECORDS join on, source → target. An arrow says a receipt
   *  follows an order; this says which column of the receipt holds the order's
   *  number, and it is what a process trail walks. Filled in on save when the
   *  designer leaves it blank (pre-fill, then lookup, then key). */
  match?: Array<{ from: string; to: string }>
  /** Whether this next step raises a task in people's worklists. Off, and the
   *  arrow is sequence only — it draws, and nothing is compiled. */
  raises_task?: boolean
  rule?: FlowRule
}

export interface FlowDoc {
  name: string
  title: string
  description: string
  nodes: FlowNode[]
  edges: FlowEdge[]
}

/** One problem with a flow, attached to the thing it is about. */
export interface FlowFinding {
  code: string
  severity: 'error' | 'warning' | 'info' | string
  /** What is wrong, in the designer's own vocabulary. */
  message: string
  /** What to do about it. */
  fix: string
  node_id?: string
  edge_id?: string
}

export interface PaletteObject {
  obj_name: string
  obj_type: string
  type_label: string
  label: string
  description?: string
  icon?: string
  /** What the metadata suggests this object IS — master data, or a step. */
  role_hint?: NodeKind
  in_navigation?: boolean
}

export interface MatchColumn {
  name: string
  label: string
  key: boolean
  table: string
  form_no: number
}

export interface FlowPalette {
  /** The transaction objects — steps, entities and data stores alike, told
   *  apart by the `kind` on the node rather than by where they came from. */
  objects: PaletteObject[]
  /** Reports, dashboards, visuals, pages, general processes and document
   *  repositories. Real things a diagram may point at, kept off the palette
   *  until asked for: a list of everything in a project is a list nobody
   *  reads. */
  extras: PaletteObject[]
  flows: Array<{ name: string; title: string }>
  /** obj_name → the DB columns a record match may join on. */
  columns: Record<string, MatchColumn[]>
  roles: string[]
}

export interface FlowGraph {
  nodes: FlowNode[]
  edges: FlowEdge[]
}

export async function listProcessFlows(project: string): Promise<{
  flows: Array<{ name: string; title: string; description: string; work_edges: number }>
}> {
  const res = await api.get('/designers/process-flows', { params: { project } })
  return res.data
}

export async function getProcessFlow(project: string, name: string): Promise<{
  flow: FlowDoc
  derived: FlowGraph
  palette: FlowPalette
  review: FlowFinding[]
}> {
  const res = await api.get('/designers/process-flow', { params: { project, name } })
  return res.data
}

/** The derived half — what the existing models already imply — plus the
 *  palette of everything that can be placed on a diagram. */
export async function processFlowGraph(project: string): Promise<{
  derived: FlowGraph
  palette: FlowPalette
}> {
  const res = await api.get('/designers/process-flow-graph', { params: { project } })
  return res.data
}

export async function saveProcessFlow(
  project: string, flow: FlowDoc, originalName?: string,
): Promise<{
  flow: FlowDoc
  compiled: { objects: Record<string, { managed: number; preserved: number }> }
  /** How each edge's record match was resolved: authored / seed / lookup / key. */
  matches?: Record<string, string>
  /** Every problem, each one pointing at the node or arrow it is about. */
  review?: FlowFinding[]
  /** Edge ids still with nothing to join records on — they draw, but no trail
   *  walks them. */
  unjoined?: string[]
}> {
  const res = await api.post('/designers/process-flow', {
    project, flow, ...(originalName ? { original_name: originalName } : {}),
  })
  return res.data
}

export async function deleteProcessFlow(project: string, name: string): Promise<{
  deleted: string
}> {
  const res = await api.delete('/designers/process-flow', { params: { project, name } })
  return res.data
}
